/**
 * Audit Log Page
 *
 * Complete history of all compliance actions with filtering and export.
 * Migrated to Tailwind + shadcn/ui
 */

import React, { useState, useMemo } from 'react';
import {
  ScrollText,
  Download,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import {
  useAuditLogs,
  useAuditStats,
  useChainVerification,
  useExportAuditLogs,
} from '../../hooks';
import { useTestMode } from '../../contexts/TestModeContext';
import AuditLogFilters from './AuditLogFilters';
import AuditLogDetail from './AuditLogDetail';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { cn } from '../../lib/utils';

const PAGE_SIZE = 25;

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAction(action) {
  // Convert applicant.created -> Applicant Created
  return action
    .split('.')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).replace(/_/g, ' '))
    .join(' - ');
}

function getActionVariant(action) {
  if (action.includes('created')) return 'success';
  if (action.includes('approved')) return 'success';
  if (action.includes('rejected')) return 'destructive';
  if (action.includes('deleted')) return 'destructive';
  if (action.includes('updated')) return 'warning';
  if (action.includes('resolved')) return 'info';
  return 'secondary';
}

function truncateId(id) {
  if (!id) return '-';
  const str = String(id);
  if (str.length <= 12) return str;
  return `${str.slice(0, 8)}...`;
}

export default function AuditLogPage() {
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const { isTestMode } = useTestMode();

  const queryFilters = useMemo(
    () => ({
      ...filters,
      is_sandbox: isTestMode,
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
      sort_order: 'desc',
    }),
    [filters, currentPage, isTestMode]
  );

  const { data, isLoading, error } = useAuditLogs(queryFilters);
  const { data: stats } = useAuditStats({ is_sandbox: isTestMode });
  const { data: verification, isLoading: verifying } = useChainVerification();
  const exportAuditLogs = useExportAuditLogs();

  const entries = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(0);
  };

  const handleExport = () => {
    exportAuditLogs.mutate({ ...filters, is_sandbox: isTestMode });
  };

  return (
    <div className="max-w-[1400px]">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-6 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <ScrollText className="w-8 h-8 text-teal-500" />
          <div>
            <h1 className="text-2xl font-semibold text-grey-900 dark:text-grey-50 m-0">
              Audit Log
            </h1>
            <p className="text-sm text-grey-500 dark:text-grey-400 mt-1">
              Complete history of all compliance actions
            </p>
          </div>
        </div>

        <div className="flex gap-3 items-center">
          {/* Chain Status */}
          {verifying ? (
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-grey-100 dark:bg-grey-800 text-sm text-grey-500 dark:text-grey-400">
              <Loader2 size={16} className="animate-spin" />
              Verifying chain...
            </div>
          ) : verification?.is_valid ? (
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-green-500/10 text-sm text-green-500 font-medium">
              <ShieldCheck size={16} />
              Chain verified
            </div>
          ) : verification?.is_valid === false ? (
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-red-500/10 text-sm text-red-500 font-medium">
              <ShieldAlert size={16} />
              Chain integrity issue
            </div>
          ) : null}

          <Button
            variant="secondary"
            onClick={handleExport}
            disabled={exportAuditLogs.isPending}
          >
            {exportAuditLogs.isPending ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download size={16} />
                Export CSV
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider mb-1">
              Total Entries
            </div>
            <div className="text-2xl font-semibold text-grey-900 dark:text-grey-50">
              {stats.total_entries.toLocaleString()}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider mb-1">
              Today
            </div>
            <div className="text-2xl font-semibold text-grey-900 dark:text-grey-50">
              {stats.entries_today.toLocaleString()}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider mb-1">
              This Week
            </div>
            <div className="text-2xl font-semibold text-grey-900 dark:text-grey-50">
              {stats.entries_this_week.toLocaleString()}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider mb-1">
              This Month
            </div>
            <div className="text-2xl font-semibold text-grey-900 dark:text-grey-50">
              {stats.entries_this_month.toLocaleString()}
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <AuditLogFilters filters={filters} onFilterChange={handleFilterChange} />

      {/* Content Card */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-red-500">
            <AlertCircle size={32} />
            <div className="mt-3 text-sm">Failed to load audit logs</div>
          </div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center">
            <ScrollText className="w-12 h-12 text-grey-300 dark:text-grey-600 mx-auto mb-4" />
            <div className="text-grey-500 dark:text-grey-400">No audit log entries found</div>
            <div className="text-sm text-grey-400 dark:text-grey-500 mt-2">
              Try adjusting your filters
            </div>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-grey-50 dark:bg-grey-800/50 border-b border-grey-200 dark:border-grey-700">
                    <th className="text-left px-4 py-3.5 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider whitespace-nowrap">
                      Timestamp
                    </th>
                    <th className="text-left px-4 py-3.5 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider whitespace-nowrap">
                      User
                    </th>
                    <th className="text-left px-4 py-3.5 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider whitespace-nowrap">
                      Action
                    </th>
                    <th className="text-left px-4 py-3.5 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider whitespace-nowrap">
                      Resource Type
                    </th>
                    <th className="text-left px-4 py-3.5 text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider whitespace-nowrap">
                      Resource ID
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-b border-grey-200 dark:border-grey-700 last:border-b-0 cursor-pointer transition-colors hover:bg-grey-50 dark:hover:bg-grey-800/50"
                      onClick={() => setSelectedEntry(entry)}
                    >
                      <td className="px-4 py-3.5 text-sm text-grey-500 dark:text-grey-400 whitespace-nowrap">
                        {formatDate(entry.created_at)}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex flex-col">
                          <span className="text-sm text-grey-900 dark:text-grey-50">
                            {entry.user_email || 'System'}
                          </span>
                          {entry.ip_address && (
                            <span className="text-xs text-grey-400 dark:text-grey-500">
                              {entry.ip_address}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <Badge variant={getActionVariant(entry.action)}>
                          {formatAction(entry.action)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="inline-flex px-2 py-1 bg-grey-100 dark:bg-grey-800 rounded text-xs text-grey-600 dark:text-grey-400 capitalize">
                          {entry.resource_type}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className="font-mono text-xs text-grey-500 dark:text-grey-400"
                          title={entry.resource_id}
                        >
                          {truncateId(entry.resource_id)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-4 border-t border-grey-200 dark:border-grey-700">
              <div className="text-sm text-grey-500 dark:text-grey-400">
                Showing {currentPage * PAGE_SIZE + 1}-
                {Math.min((currentPage + 1) * PAGE_SIZE, total)} of {total}
              </div>
              <div className="flex gap-2">
                <button
                  className={cn(
                    'flex items-center justify-center w-9 h-9 rounded-lg border transition-colors',
                    currentPage === 0
                      ? 'bg-grey-50 dark:bg-grey-800 border-grey-200 dark:border-grey-700 text-grey-300 dark:text-grey-600 cursor-not-allowed'
                      : 'bg-grey-50 dark:bg-grey-800 border-grey-200 dark:border-grey-700 text-grey-900 dark:text-grey-50 hover:border-teal-500 cursor-pointer'
                  )}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  className={cn(
                    'flex items-center justify-center w-9 h-9 rounded-lg border transition-colors',
                    currentPage >= totalPages - 1
                      ? 'bg-grey-50 dark:bg-grey-800 border-grey-200 dark:border-grey-700 text-grey-300 dark:text-grey-600 cursor-not-allowed'
                      : 'bg-grey-50 dark:bg-grey-800 border-grey-200 dark:border-grey-700 text-grey-900 dark:text-grey-50 hover:border-teal-500 cursor-pointer'
                  )}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage >= totalPages - 1}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </Card>

      {selectedEntry && (
        <AuditLogDetail
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
        />
      )}
    </div>
  );
}
